import React, {useEffect, useRef, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import throttle from 'lodash/throttle'
import {isNil} from 'ramda'
import {SpacingPropsType} from 'types/Layout'

import {useDeviceLayout} from '../../../../context/deviceLayout'
import {cssTheme} from '../../../../themes'
import {Button, ButtonSize} from '../../../designSystem/Button'
import {Icon} from '../../../designSystem/Icon'

const THROTTLING_INTERVAL = 100 // ms

interface Props {
  /** Elements that will be rendered inside the carousel */
  children: React.ReactNode
  /** Whether the language is RTL or not */
  isRtlLanguage?: boolean
  /** Custom prop to hide nav buttons only on mobileSm resolution (false by default) */
  hideNavButtonsOnlyForMobileSm?: boolean
  /** A function to run when scroll happens */
  onScroll?: () => void
  hasSidePaddings?: boolean
  itemsGap?: SpacingPropsType
  /** Navigation buttons size */
  navigationButtonSize?: ButtonSize
}

const ScrollWrapper = styled.div<{
  nextButtonVisibility?: boolean
  previousButtonVisibility?: boolean
  itemsGap: SpacingPropsType
}>`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;

  /* hide scrollbar */
  -ms-overflow-style: none; /* for Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }
  overflow-scrolling: touch;

  scroll-snap-type: x mandatory;
  scroll-padding: 0 ${cssTheme.layout.spacing.s200};

  /* A mask to gradually fade out the cards when they are not fully visible from left and right sides*/
  mask-image: linear-gradient(
    to right,
    rgba(
        0,
        0,
        0,
        ${({previousButtonVisibility}) => (previousButtonVisibility ? 0 : 1)}
      )
      0%,
    rgba(0, 0, 0, 1) 15%,
    rgba(0, 0, 0, 1) 85%,
    rgba(0, 0, 0, ${({nextButtonVisibility}) => (nextButtonVisibility ? 0 : 1)})
      100%
  );
  > * + * {
    margin-left: ${({itemsGap}) => cssTheme.layout.spacing[itemsGap]};
  }
  &.hasSidePaddings {
    padding-left: ${cssTheme.layout.spacing.s500};
    padding-right: ${cssTheme.layout.spacing.s500};
  }
  /* The end margin of SmoothScrollNavigation is not taken into account.
    * See this thread: https://stackoverflow.com/questions/38993170 */
  &:after {
    content: '';
    flex: 0 0 1px;
  }
`

const CardsAndNavButtonsWrapper = styled.div`
  position: relative;
`

const NavigateButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  margin: ${cssTheme.layout.spacing.s200} 0;
  &.direction-prev {
    left: 0;
  }
  &.direction-next {
    right: 0;
  }
  [dir='rtl'] & {
    transform: scale(-1);
  }
`

export const SmoothScrollNavigation = ({
  children,
  isRtlLanguage = false,
  hideNavButtonsOnlyForMobileSm,
  onScroll,
  hasSidePaddings,
  itemsGap = 's500',
  navigationButtonSize = 'sm'
}: Props) => {
  const {isMobile, isMobileSm} = useDeviceLayout()
  const [cardsPerClick, setCardsPerClick] = useState(2)
  const [cardWidth, setCardWidth] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)

  const scrollWrapperRef = useRef<HTMLDivElement>(null)
  const [navButtonVisibility, setNavButtonVisibility] = useState({
    next: false,
    prev: false
  })

  useEffect(() => {
    const ref = scrollWrapperRef.current

    if (!ref) {
      return
    }

    const throttledSetScrollPosition = throttle(() => {
      setScrollPosition(Math.round(ref.scrollLeft))
    }, THROTTLING_INTERVAL)

    const handleScroll = () => {
      throttledSetScrollPosition()
      onScroll?.()
    }

    ref.addEventListener('scroll', handleScroll)
    return () => ref.removeEventListener('scroll', handleScroll)
  }, [])

  const container = scrollWrapperRef.current
  const {
    clientWidth,
    scrollWidth,
    scrollLeft: containerScrollLeft
  } = container || {}

  const scrollLeft =
    isRtlLanguage && containerScrollLeft
      ? Math.abs(containerScrollLeft)
      : containerScrollLeft

  useEffect(() => {
    const isHideNavButtons = hideNavButtonsOnlyForMobileSm
      ? isMobileSm
      : isMobile

    if (isHideNavButtons) {
      setNavButtonVisibility({
        next: false,
        prev: false
      })
      return
    }

    if (!isNil(scrollLeft) && !isNil(clientWidth) && !isNil(scrollWidth)) {
      const thresholdFromSides = 15

      const nextButtonVisibility =
        scrollLeft + clientWidth < scrollWidth - thresholdFromSides
      const prevButtonVisibility = scrollLeft > thresholdFromSides

      setNavButtonVisibility({
        next: nextButtonVisibility,
        prev: prevButtonVisibility
      })
    }
  }, [
    scrollPosition,
    scrollWrapperRef,
    isMobile,
    isRtlLanguage,
    isMobileSm,
    hideNavButtonsOnlyForMobileSm,
    container,
    scrollLeft,
    clientWidth,
    scrollWidth
  ])

  useEffect(() => {
    const container = scrollWrapperRef.current
    if (!container) {
      return
    }

    const {firstElementChild} = container
    if (!firstElementChild) {
      return
    }
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCardWidth(entry.target.getBoundingClientRect().width)
        setCardsPerClick(
          Math.floor(
            container.clientWidth / entry.target.getBoundingClientRect().width
          ) - 1
        )
      }
    })

    resizeObserver.observe(firstElementChild)

    return () => {
      resizeObserver.disconnect()
    }
  }, [children])

  const handleNavigateButtonClick = (direction: 'next' | 'prev') => () => {
    const container = scrollWrapperRef.current

    if (!container) {
      return
    }

    const scrollPerClick = cardsPerClick * cardWidth
    const scrollOffset = direction === 'next' ? -scrollPerClick : scrollPerClick
    const widthToScroll = isRtlLanguage
      ? container.scrollLeft + scrollOffset
      : container.scrollLeft - scrollOffset

    container.scroll({
      left: widthToScroll,
      behavior: 'smooth'
    })
  }

  return (
    <CardsAndNavButtonsWrapper>
      {navButtonVisibility?.prev && (
        <NavigateButtonWrapper className="direction-prev">
          <Button
            variant="secondary"
            isFloating
            isRounded
            size={navigationButtonSize}
            iconStart={<Icon name="ChevronLeft" />}
            onClick={handleNavigateButtonClick('prev')}
          />
        </NavigateButtonWrapper>
      )}
      <ScrollWrapper
        nextButtonVisibility={navButtonVisibility?.next}
        previousButtonVisibility={navButtonVisibility?.prev}
        itemsGap={itemsGap}
        ref={scrollWrapperRef}
        className={cx(hasSidePaddings && 'hasSidePaddings')}
      >
        {children}
      </ScrollWrapper>

      {navButtonVisibility?.next && (
        <NavigateButtonWrapper className="direction-next">
          <Button
            variant="secondary"
            isFloating
            isRounded
            size={navigationButtonSize}
            iconStart={<Icon name="ChevronRight" />}
            onClick={handleNavigateButtonClick('next')}
          />
        </NavigateButtonWrapper>
      )}
    </CardsAndNavButtonsWrapper>
  )
}
